import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sentinel stored in the URL param / localStorage to represent an explicit
 * "no filters selected" state. Needed because an absent param means "all
 * selected (default)", so empty selection must be encoded distinctly to
 * survive a reload or a shared deep-link.
 */
const NONE_SENTINEL = '__none__';

export interface UseUrlMultiSelectFilterOptions {
  /**
   * All available filter keys. When every key is selected the filter is
   * considered inactive: the URL param is removed and localStorage cleared.
   * Pass a stable reference (e.g. a module-scope constant) to avoid needless
   * recomputation.
   */
  allFilters: readonly string[];
  /**
   * localStorage key used to persist the selection, e.g. `vs-filter-vehicles`.
   * Keep the `vs-filter-` prefix (see docs/NAMING.md — legacy key prefix).
   */
  storageKey: string;
  /** URL search-param name. Defaults to `filter`. */
  paramName?: string;
}

export interface UseUrlMultiSelectFilterResult {
  /** Currently selected filter keys. An empty array means "none selected". */
  selectedFilters: string[];
  /** Replace the whole selection. */
  setSelectedFilters: (filters: string[]) => void;
  /** Toggle a single filter key on/off. */
  toggleFilter: (filterKey: string) => void;
  /** True when every available filter is selected (the default, inactive state). */
  allSelected: boolean;
}

/**
 * Domain-agnostic multi-select filter state persisted in a single URL search
 * param plus localStorage. Three states are encoded on one param:
 *
 * - **All selected (default):** param absent, localStorage key removed.
 * - **None selected:** param and localStorage set to `__none__` → `[]`.
 * - **Partial:** comma-joined keys, e.g. `filter=overdue,recent`.
 *
 * Precedence is URL param → localStorage → all. On mount a one-time effect
 * hydrates the URL from localStorage when no param is present.
 *
 * Extracted from the previously duplicated logic in VehiclesPage,
 * InventoryPage, InspectionsPage and ApprovalsPage (issue #878).
 */
export function useUrlMultiSelectFilter({
  allFilters,
  storageKey,
  paramName = 'filter',
}: UseUrlMultiSelectFilterOptions): UseUrlMultiSelectFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedFilters = useMemo(() => {
    const param = searchParams.get(paramName);
    if (!param) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored === NONE_SENTINEL) return [];
        if (stored) {
          const parsed = stored.split(',').filter(Boolean);
          if (parsed.length > 0) return parsed;
        }
      } catch { /* ignore */ }
      return [...allFilters];
    }
    if (param === NONE_SENTINEL) return [];
    const parsed = param.split(',').filter(Boolean);
    return parsed.length > 0 ? parsed : [...allFilters];
  }, [searchParams, paramName, storageKey, allFilters]);

  const setSelectedFilters = useCallback((filters: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (filters.length === 0) {
      next.set(paramName, NONE_SENTINEL);
      try { localStorage.setItem(storageKey, NONE_SENTINEL); } catch { /* ignore */ }
    } else if (filters.length === allFilters.length && allFilters.every((s) => filters.includes(s))) {
      next.delete(paramName);
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    } else {
      next.set(paramName, filters.join(','));
      try { localStorage.setItem(storageKey, filters.join(',')); } catch { /* ignore */ }
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, paramName, storageKey, allFilters]);

  // Sync localStorage → URL on mount when no URL param is present.
  const didSyncRef = useRef(false);
  useEffect(() => {
    if (didSyncRef.current) return;
    didSyncRef.current = true;
    if (!searchParams.has(paramName)) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const next = new URLSearchParams(searchParams);
          next.set(paramName, stored);
          setSearchParams(next, { replace: true });
        }
      } catch { /* ignore */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFilter = useCallback((filterKey: string) => {
    if (selectedFilters.includes(filterKey)) {
      setSelectedFilters(selectedFilters.filter((s) => s !== filterKey));
    } else {
      setSelectedFilters([...selectedFilters, filterKey]);
    }
  }, [selectedFilters, setSelectedFilters]);

  const allSelected =
    selectedFilters.length === allFilters.length &&
    allFilters.every((s) => selectedFilters.includes(s));

  return { selectedFilters, setSelectedFilters, toggleFilter, allSelected };
}
